import React from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionGuard } from '@/components/layout';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useCostCenterExport, useCostCentersList } from '@/hooks/organization';
import { SECTIONS } from '@/types/sections';


export const CostCentersTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    openCreatePanel,
    loading,
    debouncedSearchText,
  } = useCostCentersList();
  const { exportCostCenters, isExporting } = useCostCenterExport({
    searchText: debouncedSearchText || undefined,
    status: filters.status,
  });

  return (
    <div className="flex flex-col gap-6" data-testid="cost-centers-tab">
      <div className="flex justify-between items-start">
        <PermissionGuard
          action="create"
          fallback="hidden"
          denied={!canSection(SECTIONS.CENTROS_COSTO, 'create')}
        >
          <PrimaryButton
            iconLeft={<IconScout name="plus" />}
            onClick={openCreatePanel}
          >
            {translate('organization:actions.createCostCenter')}
          </PrimaryButton>
        </PermissionGuard>

        <ExportDropdown
          onExport={exportCostCenters}
          placement="right"
          isLoading={isExporting}
          availableFormats={['EXCEL']}
        />
      </div>

      <div className="flex flex-wrap gap-4">
        <FilterDropdown
          filters={filterConfig}
          values={{
            status: filters.status,
          }}
          onChangeFilter={(key, value) => {
            setFilters((previous) => ({
              ...previous,
              [key]: value,
            }));
          }}
        />
      </div>

      <Table
        columns={columns}
        data={rows}
        totalRows={total}
        searchable
        onSearch={onSearch}
        columnMaxWidth={180}
        pagination={{
          page,
          pageSize,
          total,
          onChange: setPage,
          onPageSizeChange: setPageSize,
        }}
        data-testid="cost-centers-table"
        isLoading={loading}
        actions={actions}
      />
    </div>
  );
};

export default CostCentersTab;
